// NEW_FILE_JUSTIFIED: Single source of truth for builder field-type metadata; consumed by Toolbox, Inspector, FieldCard
import {
  Type,
  AlignLeft,
  Hash,
  Mail,
  Phone as PhoneIcon, // AS_JUSTIFIED: import rename to avoid collision with native 'Phone' name; not a type assertion
  MapPin,
  Calendar as CalendarIcon, // AS_JUSTIFIED: import rename to avoid collision with native 'Calendar' name; not a type assertion
  ChevronDown,
  ListChecks,
  CheckSquare,
  Upload,
} from 'lucide-react';
import type {
  TBuilderFieldType,
  IFieldTypeMeta,
  TApplicableValidation,
} from '@/@types/insuranceBuilder';

export const FIELD_TYPE_REGISTRY: Record<TBuilderFieldType, IFieldTypeMeta> = {
  text: {
    type: 'text',
    label: 'Text',
    icon: Type,
    description: 'Single-line text input',
    applicableValidation: ['minLength', 'maxLength', 'regex'],
    supportsOptionsSource: false,
    supportsPlaceholder: true,
  },
  textarea: {
    type: 'textarea',
    label: 'Text area',
    icon: AlignLeft,
    description: 'Multi-line text input',
    applicableValidation: ['minLength', 'maxLength'],
    supportsOptionsSource: false,
    supportsPlaceholder: true,
  },
  number: {
    type: 'number',
    label: 'Number',
    icon: Hash,
    description: 'Numeric input',
    applicableValidation: ['min', 'max', 'integerOnly'],
    supportsOptionsSource: false,
    supportsPlaceholder: true,
  },
  email: {
    type: 'email',
    label: 'Email',
    icon: Mail,
    description: 'Email address (built-in format check)',
    applicableValidation: [],
    supportsOptionsSource: false,
    supportsPlaceholder: true,
  },
  phone: {
    type: 'phone',
    label: 'Phone',
    icon: PhoneIcon,
    description: 'Phone number',
    applicableValidation: ['regex'],
    supportsOptionsSource: false,
    supportsPlaceholder: true,
  },
  postalCode: {
    type: 'postalCode',
    label: 'Postal code',
    icon: MapPin,
    description: 'Postal/ZIP code (numeric input mode)',
    applicableValidation: ['regex'],
    supportsOptionsSource: false,
    supportsPlaceholder: true,
  },
  date: {
    type: 'date',
    label: 'Date',
    icon: CalendarIcon,
    description: 'Date picker',
    applicableValidation: ['minDate', 'maxDate', 'mustBePast', 'mustBeFuture'],
    supportsOptionsSource: false,
    supportsPlaceholder: false,
  },
  select: {
    type: 'select',
    label: 'Select',
    icon: ChevronDown,
    description: 'Single-choice dropdown',
    applicableValidation: [],
    supportsOptionsSource: true,
    supportsPlaceholder: true,
  },
  multiSelect: {
    type: 'multiSelect',
    label: 'Multi-select',
    icon: ListChecks,
    description: 'Multi-choice dropdown',
    applicableValidation: [],
    supportsOptionsSource: true,
    supportsPlaceholder: true,
  },
  checkbox: {
    type: 'checkbox',
    label: 'Checkbox',
    icon: CheckSquare,
    description: 'Single yes/no toggle',
    applicableValidation: [],
    supportsOptionsSource: false,
    supportsPlaceholder: false,
  },
  file: {
    type: 'file',
    label: 'File upload',
    icon: Upload,
    description: 'File upload with optional size/type limits',
    applicableValidation: ['maxSizeMB', 'acceptedMimeTypes'],
    supportsOptionsSource: false,
    supportsPlaceholder: false,
  },
};

// Toolbox order — controls left-toolbox button order
export const TOOLBOX_FIELD_ORDER: TBuilderFieldType[] = [
  'text',
  'textarea',
  'number',
  'email',
  'phone',
  'postalCode',
  'date',
  'select',
  'multiSelect',
  'checkbox',
  'file',
];

export function getFieldMeta(type: TBuilderFieldType): IFieldTypeMeta {
  return FIELD_TYPE_REGISTRY[type];
}

// Re-export for consumers that import TApplicableValidation from this module
export type { TApplicableValidation };
